import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/components/shared";
import { Printer, ArrowLeft } from "lucide-react";

// -------- Indian number-to-words --------------------------------------------
import { formatDate, formatDateTime } from "@/lib/datetime";
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function belowThousand(n) {
  if (n === 0) return "";
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
  return ONES[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + belowThousand(n % 100) : "");
}

function inrInWords(amount) {
  if (amount === null || amount === undefined) return "";
  const rounded = Math.round(amount * 100);
  const rupees = Math.floor(rounded / 100);
  const paise = rounded % 100;
  if (rupees === 0 && paise === 0) return "Rupees Zero Only";
  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(belowThousand(crore) + " Crore");
  if (lakh) parts.push(belowThousand(lakh) + " Lakh");
  if (thousand) parts.push(belowThousand(thousand) + " Thousand");
  if (rest) parts.push(belowThousand(rest));
  let words = "Rupees " + parts.join(" ");
  if (paise) words += " and " + belowThousand(paise) + " Paise";
  return words + " Only";
}

export default function GRNPrint() {
  const { id } = useParams();
  const [purchase, setPurchase] = useState(null);
  const [rmsById, setRmsById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, rms] = await Promise.all([
          api.get(`/production/rm-purchases/${id}`),
          api.get("/production/raw-materials"),
        ]);
        setPurchase(p.data);
        const map = {};
        (rms.data || []).forEach((r) => { map[r.id] = r; });
        setRmsById(map);
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (!loading && purchase) {
      setTimeout(() => window.print(), 400);
    }
  }, [loading, purchase]);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!purchase) return <div className="p-8 text-red-600">Purchase not found</div>;

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="font-heading font-semibold text-gray-900">Print Preview — {purchase.purchase_no}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="grn-print-btn">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none p-8 my-6 print:my-0 print:p-0 text-gray-900">
        {/* Header */}
        <div className="flex justify-between items-start border-b-2 border-[#9B1C1C] pb-4 mb-5">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#9B1C1C]">Thara Gold Covering</h1>
            <p className="text-xs text-gray-600 mt-1">Imitation Jewellery — Manufacturer & Wholesaler</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Goods Receipt Note</p>
            <p className="text-lg font-heading font-bold text-gray-900">{purchase.purchase_no}</p>
          </div>
        </div>

        {/* Vendor + Meta */}
        <div className="grid grid-cols-2 gap-6 mb-5 text-sm">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Vendor</p>
            <p className="font-semibold text-base">{purchase.vendor_name}</p>
            {purchase.vendor_gst && <p className="text-xs mt-1"><span className="text-gray-500">GST:</span> <span className="font-mono">{purchase.vendor_gst}</span></p>}
          </div>
          <div>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Date</td><td className="py-0.5 font-mono">{formatDate(purchase.purchase_date)}</td></tr>
                <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Invoice #</td><td className="py-0.5 font-mono">{purchase.invoice_no}</td></tr>
                {purchase.reference_no && <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Reference</td><td className="py-0.5 font-mono">{purchase.reference_no}</td></tr>}
                <tr><td className="py-0.5 text-gray-500 uppercase tracking-widest text-[10px]">Payment</td><td className="py-0.5 font-semibold">{purchase.payment_status}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full text-xs border border-gray-300 mb-5">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-300 px-2 py-1.5 text-left w-8">#</th>
              <th className="border border-gray-300 px-2 py-1.5 text-left">Description</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-16">HSN</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-14">Unit</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-16">Qty</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Rate</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Discount</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-14">Tax %</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-20">Tax Amt</th>
              <th className="border border-gray-300 px-2 py-1.5 text-right w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {purchase.items.map((l, i) => {
              const rm = rmsById[l.raw_material_id];
              return (
                <tr key={i}>
                  <td className="border border-gray-300 px-2 py-1 text-center">{i + 1}</td>
                  <td className="border border-gray-300 px-2 py-1">
                    <div className="font-medium">{l.raw_material_name}</div>
                    {l.notes && <div className="text-[10px] text-gray-500">{l.notes}</div>}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 text-center font-mono">{rm?.hsn_code || "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-center">{l.unit}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.qty}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{formatINR(l.purchase_price)}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.discount ? formatINR(l.discount) : "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.tax_pct || "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums">{l.tax_amount ? formatINR(l.tax_amount) : "—"}</td>
                  <td className="border border-gray-300 px-2 py-1 text-right tabular-nums font-semibold">{formatINR(l.line_amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals + amount in words */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Amount in Words</p>
            <p className="text-sm font-semibold italic border-b border-gray-300 pb-2">{inrInWords(purchase.net_amount)}</p>
            {purchase.payment_notes && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 mb-1">Payment Notes</p>
                <p className="text-xs text-gray-700">{purchase.payment_notes}</p>
              </div>
            )}
          </div>
          <div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1 text-gray-600">Subtotal</td><td className="py-1 text-right tabular-nums font-mono">{formatINR(purchase.subtotal)}</td></tr>
                {purchase.discount_total > 0 && <tr><td className="py-1 text-gray-600">Line Discount</td><td className="py-1 text-right tabular-nums font-mono">−{formatINR(purchase.discount_total)}</td></tr>}
                {purchase.header_discount > 0 && <tr><td className="py-1 text-gray-600">Header Discount</td><td className="py-1 text-right tabular-nums font-mono">−{formatINR(purchase.header_discount)}</td></tr>}
                {purchase.tax_total > 0 && <tr><td className="py-1 text-gray-600">Tax (GST)</td><td className="py-1 text-right tabular-nums font-mono">+{formatINR(purchase.tax_total)}</td></tr>}
                <tr className="border-t-2 border-gray-900">
                  <td className="py-2 font-bold text-lg">Net Amount</td>
                  <td className="py-2 text-right tabular-nums font-heading font-bold text-lg">{formatINR(purchase.net_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Signatory */}
        <div className="mt-16 grid grid-cols-2 gap-6 text-xs">
          <div>
            <p className="border-t border-gray-400 pt-1 text-center text-gray-500">Received By</p>
          </div>
          <div>
            <p className="border-t border-gray-400 pt-1 text-center text-gray-500">For Thara Gold Covering — Authorized Signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}
